package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.model.AdminWeekday
import org.gymly.model.AdminWorkDaySchedule
import org.gymly.model.AdminWorkTimeBlock
import org.gymly.theme.AdminColors

internal data class HoursBlockDraft(val start: String = "06:00", val end: String = "12:00")

internal data class HoursDayDraft(
    val enabled: Boolean = false,
    val blocks: List<HoursBlockDraft> = listOf(HoursBlockDraft()),
)

internal fun initialHoursDayDrafts(schedule: List<AdminWorkDaySchedule>?): Map<Int, HoursDayDraft> {
    val byDay = schedule.orEmpty().associateBy { it.weekday }
    return AdminWeekday.entries.associate { day ->
        val existing = byDay[day.iso]
        day.iso to if (existing == null || existing.blocks.isEmpty()) {
            HoursDayDraft()
        } else {
            HoursDayDraft(
                enabled = true,
                blocks = existing.blocks.map {
                    HoursBlockDraft(
                        start = it.startTime.trim().take(5).ifBlank { "06:00" },
                        end = it.endTime.trim().take(5).ifBlank { "12:00" },
                    )
                },
            )
        }
    }
}

internal fun normalizeHoursTimeInput(raw: String): String? {
    val t = raw.trim()
    if (t.isEmpty()) return null
    val hhmm = t.take(5)
    if (!Regex("""^\d{2}:\d{2}$""").matches(hhmm)) return null
    val h = hhmm.substring(0, 2).toIntOrNull() ?: return null
    val m = hhmm.substring(3, 5).toIntOrNull() ?: return null
    if (h !in 0..23 || m !in 0..59) return null
    return "$hhmm:00"
}

/** Construye el schedule o devuelve mensaje de error. */
internal fun buildHoursSchedule(
    dayDrafts: Map<Int, HoursDayDraft>,
): Pair<List<AdminWorkDaySchedule>?, String?> {
    val schedule = mutableListOf<AdminWorkDaySchedule>()
    for (day in AdminWeekday.entries) {
        val draft = dayDrafts[day.iso] ?: continue
        if (!draft.enabled) continue
        if (draft.blocks.isEmpty()) {
            return null to "${day.labelEs}: agrega al menos un bloque horario."
        }
        val blocks = mutableListOf<AdminWorkTimeBlock>()
        for ((i, block) in draft.blocks.withIndex()) {
            val startNorm = normalizeHoursTimeInput(block.start)
            val endNorm = normalizeHoursTimeInput(block.end)
            if (startNorm == null || endNorm == null) {
                return null to "${day.labelEs}: bloque ${i + 1} inválido. Usa HH:mm."
            }
            if (endNorm <= startNorm) {
                return null to "${day.labelEs}: en el bloque ${i + 1} el fin debe ser posterior al inicio."
            }
            blocks.add(AdminWorkTimeBlock(startTime = startNorm, endTime = endNorm))
        }
        val sorted = blocks.sortedBy { it.startTime }
        for (i in 1 until sorted.size) {
            if (sorted[i].startTime < sorted[i - 1].endTime) {
                return null to "${day.labelEs}: los bloques no pueden solaparse."
            }
        }
        schedule.add(AdminWorkDaySchedule(weekday = day.iso, blocks = sorted))
    }
    return schedule to null
}

internal fun formatOperatingHoursHint(schedule: List<AdminWorkDaySchedule>): String {
    if (schedule.isEmpty()) return ""
    return schedule.joinToString(" · ") { day ->
        val name = AdminWeekday.entries.firstOrNull { it.iso == day.weekday }?.shortEs ?: "?"
        val ranges = day.blocks.joinToString(", ") { b ->
            "${b.startTime.take(5)}–${b.endTime.take(5)}"
        }
        "$name $ranges"
    }
}

/** Comprueba que cada bloque del trabajador quepa en algún bloque de la empresa el mismo día. */
internal fun validateWithinCompanyHours(
    worker: List<AdminWorkDaySchedule>,
    company: List<AdminWorkDaySchedule>,
): String? {
    if (worker.isEmpty()) return null
    if (company.isEmpty()) {
        return "Configura primero el horario de atención en Empresa; la disponibilidad del trabajador se valida contra ese horario."
    }
    val companyByDay = company.associateBy { it.weekday }
    for (day in worker) {
        val open = companyByDay[day.weekday]
        val dayName = AdminWeekday.entries.firstOrNull { it.iso == day.weekday }?.labelEs
            ?: "día ${day.weekday}"
        if (open == null || open.blocks.isEmpty()) {
            return "La empresa no abre el $dayName; quita ese día de la jornada."
        }
        for (block in day.blocks) {
            val fits = open.blocks.any { c ->
                block.startTime >= c.startTime && block.endTime <= c.endTime
            }
            if (!fits) {
                return "La jornada del $dayName (${block.startTime.take(5)}–${block.endTime.take(5)}) " +
                    "queda fuera del horario de la empresa."
            }
        }
    }
    return null
}

@Composable
internal fun OperatingHoursEditor(
    dayDrafts: Map<Int, HoursDayDraft>,
    onDayDraftsChange: (Map<Int, HoursDayDraft>) -> Unit,
    enabled: Boolean = true,
    hint: String? = null,
) {
    fun updateDay(iso: Int, transform: (HoursDayDraft) -> HoursDayDraft) {
        onDayDraftsChange(dayDrafts.toMutableMap().also { it[iso] = transform(it[iso] ?: HoursDayDraft()) })
    }

    hint?.let {
        Text(
            it,
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
            modifier = Modifier.padding(bottom = 8.dp),
        )
    }
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        // Celular: el día arriba y sus bloques debajo; escritorio: día a la izquierda y bloques a la derecha.
        val narrow = maxWidth < 600.dp
        Column(Modifier.fillMaxWidth()) {
            AdminWeekday.entries.forEach { day ->
                val draft = dayDrafts[day.iso] ?: HoursDayDraft()
                val container = Modifier
                    .fillMaxWidth()
                    .background(if (draft.enabled) AdminColors.Primary.copy(alpha = 0.04f) else Color.Transparent)
                    .padding(vertical = 10.dp, horizontal = 4.dp)
                val dayHeader: @Composable (Modifier) -> Unit = { mod ->
                    Row(mod, verticalAlignment = Alignment.CenterVertically) {
                        Checkbox(
                            checked = draft.enabled,
                            onCheckedChange = { checked ->
                                updateDay(day.iso) {
                                    it.copy(
                                        enabled = checked,
                                        blocks = if (checked && it.blocks.isEmpty()) {
                                            listOf(HoursBlockDraft())
                                        } else {
                                            it.blocks
                                        },
                                    )
                                }
                            },
                            enabled = enabled,
                            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                        )
                        Text(
                            day.labelEs,
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp,
                            color = AdminColors.Text,
                        )
                    }
                }
                val dayBlocks: @Composable (Modifier) -> Unit = { mod ->
                    if (!draft.enabled) {
                        Text(
                            "Cerrado",
                            color = AdminColors.TextMuted,
                            fontSize = 13.sp,
                            modifier = mod.padding(top = 14.dp),
                        )
                    } else {
                        Column(mod, verticalArrangement = Arrangement.spacedBy(6.dp)) {
                            draft.blocks.forEachIndexed { index, block ->
                                Row(
                                    Modifier.fillMaxWidth(),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                                ) {
                                    OutlinedTextField(
                                        value = block.start,
                                        onValueChange = { v ->
                                            updateDay(day.iso) { d ->
                                                d.copy(blocks = d.blocks.toMutableList().also { list ->
                                                    list[index] = list[index].copy(start = v.take(5))
                                                })
                                            }
                                        },
                                        label = { Text("Desde") },
                                        placeholder = { Text("06:00") },
                                        singleLine = true,
                                        enabled = enabled,
                                        modifier = Modifier.weight(1f),
                                        colors = OutlinedTextFieldDefaults.colors(
                                            focusedBorderColor = AdminColors.Primary,
                                            unfocusedBorderColor = AdminColors.Outline,
                                        ),
                                    )
                                    Text("–", color = AdminColors.TextMuted)
                                    OutlinedTextField(
                                        value = block.end,
                                        onValueChange = { v ->
                                            updateDay(day.iso) { d ->
                                                d.copy(blocks = d.blocks.toMutableList().also { list ->
                                                    list[index] = list[index].copy(end = v.take(5))
                                                })
                                            }
                                        },
                                        label = { Text("Hasta") },
                                        placeholder = { Text("12:00") },
                                        singleLine = true,
                                        enabled = enabled,
                                        modifier = Modifier.weight(1f),
                                        colors = OutlinedTextFieldDefaults.colors(
                                            focusedBorderColor = AdminColors.Primary,
                                            unfocusedBorderColor = AdminColors.Outline,
                                        ),
                                    )
                                    if (draft.blocks.size > 1) {
                                        IconButton(
                                            onClick = {
                                                updateDay(day.iso) { d ->
                                                    d.copy(blocks = d.blocks.toMutableList().also { it.removeAt(index) })
                                                }
                                            },
                                            enabled = enabled,
                                        ) {
                                            Icon(Icons.Outlined.Delete, contentDescription = "Quitar bloque", tint = AdminColors.Error)
                                        }
                                    }
                                }
                            }
                            TextButton(
                                onClick = {
                                    updateDay(day.iso) { d ->
                                        d.copy(blocks = d.blocks + HoursBlockDraft(start = "14:00", end = "21:00"))
                                    }
                                },
                                enabled = enabled,
                            ) {
                                Icon(Icons.Outlined.Add, contentDescription = null, modifier = Modifier.padding(end = 4.dp))
                                Text("Agregar bloque")
                            }
                        }
                    }
                }
                if (narrow) {
                    Column(container, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        dayHeader(Modifier.fillMaxWidth())
                        dayBlocks(Modifier.fillMaxWidth())
                    }
                } else {
                    Row(container, verticalAlignment = Alignment.Top) {
                        dayHeader(Modifier.width(150.dp).padding(top = 4.dp))
                        dayBlocks(Modifier.weight(1f))
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.25f))
            }
        }
    }
}
